import React, { useEffect } from 'react';

import {
  Navigate, useNavigate, useParams,
} from 'react-router';

import Breadcrumbs from '../../components/Breadcrumbs';
import { LoadingWrapper } from '../../components/PageLayout';
import VideoPlayer from '../../components/VideoPlayer';
import { ResourceType } from '../../components/VideoPlayer/types';
import { useGetVideoQuery } from '../../services/videos';

import {
  BreadcrumbsWrapper,
} from './index.styles';

interface VideoPlayerPageContentProps {
  subject: string,
  videoId: string,
}

export const VideoPlayerPageContent = ({ subject, videoId }: VideoPlayerPageContentProps) => {
  const { data: video, error, isLoading: isVideoLoading } = useGetVideoQuery({ subject, videoId });
  const navigate = useNavigate();

  const { path, resources, topicResources, ...videoPlayerProps } = video?.data || {
    path: [],
    videoId: '',
    youtubeId: '',
    title: '',
    host: '',
    uploadDatetime: 0,
  };

  const handleVideoEnd = (c: string, vId: string) => {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        navigate(`/video/${c}/${vId}`);
      });
    } else {
      // If not in fullscreen, navigate to next video directly
      navigate(`/video/${c}/${vId}`);
    }
  };

  // Update page title
  useEffect(() => {
    if (video?.data.title) {
      document.title = `${video?.data.title} | Cognito Videos`;
    }
  }, [video]);

  if (error) {
    if ('status' in error) {
      if (error.status === 401) {
        return <Navigate replace to="/videos?err=no-permission" />;
      }
      return <Navigate replace to="/videos?err=no-video" />;
    }
    return <Navigate replace to="/videos?err=no-video" />;
  }

  return (
    <LoadingWrapper loading={isVideoLoading}>
      <BreadcrumbsWrapper>
        <Breadcrumbs
          title={video?.data.title || 'Unnamed Video'}
          path={path.map((p) => ({ label: p.label, href: p.href }))}
        />
      </BreadcrumbsWrapper>
      <VideoPlayer
        {...videoPlayerProps}
        resources={[
          ...(topicResources?.map((x) => ({ ...x, type: ResourceType.Topic })) ?? []),
          ...(resources?.map((x) => ({ ...x, type: ResourceType.Video })) ?? []),
        ]}
        getVideoHref={(c, vId) => `/video/${c}/${vId}`}
        onVideoEnd={handleVideoEnd}
        autoplay
      />
    </LoadingWrapper>
  );
};

const VideoPlayerPage = () => {
  const { subject, videoId } = useParams();

  if (subject && videoId) {
    return (
      <VideoPlayerPageContent
        subject={subject}
        videoId={videoId}
      />
    );
  }

  // If no subject or videoId is provided, return to Videos page
  return <Navigate replace to="/videos" />;
};

export default VideoPlayerPage;
